import type { BaseUsecase } from '@usecases/base-usecase';
import type { User } from '@entities/user';
import type {
  UserLoginEventPublisher,
  UserLoginRepository,
} from '@usecases/user-login.usecase';
import {
  isAxiosLikeError,
  MissingIntegrationOrNumberNotAssign,
} from '@usecases/user-login.usecase';
import { InvalidUserLoginRequest } from '@usecases/user-login.usecase';

export interface UserSSOLoginRequest {
  code: string;
  redirectUrl: string;
}

export interface UserSSOLoginAuthenticator {
  authenticate(credentials: UserSSOLoginRequest): Promise<User>;
}

export type UserSSOLoginUsecase = BaseUsecase<UserSSOLoginRequest, void>;

export class UserSSOLogin implements UserSSOLoginUsecase {
  constructor(
    private authenticator: UserSSOLoginAuthenticator,
    private repository: UserLoginRepository,
    private publisher: UserLoginEventPublisher,
  ) {}

  async run(req: UserSSOLoginRequest): Promise<void> {
    try {
      const user = await this.authenticator.authenticate(req);
      await Promise.all([
        this.repository.addUserDetails(user),
        this.publisher.publish(user),
      ]);
    } catch (error) {
      console.log(
        'Error inside UserLoginEventPublisher details ',
        JSON.stringify(error),
      );
      if (isAxiosLikeError(error)) {
        const status = error.response?.status ?? error.status;

        if (status === 500) {
          throw new MissingIntegrationOrNumberNotAssign(
            'No active integration found or phone number not linked to one.',
            error,
          );
        }

        // You could handle other status codes here (e.g., 401, 403, etc.)
      }
      throw new InvalidUserLoginRequest(
        'Unable to login with SSO, please try again!',
        error,
      );
    }
  }
}
